import React, { useState, useEffect } from 'react';
import axiosConfig from '../../api/axiosConfig';

const BankManagement = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentBank, setCurrentBank] = useState({
    name: '',
    code: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    setLoading(true);
    try {
      const response = await axiosConfig.get('/admin/banks');
      setBanks(response.data.banks || []);
    } catch (error) {
      console.error('Bankalar yüklenirken hata:', error);
      setBanks([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setCurrentBank({
        name: '',
        code: '',
        isActive: true
      });
      setErrors({});
      setEditMode(false);
    }
  };

  const handleEdit = (bank) => {
    setCurrentBank(bank);
    setEditMode(true);
    setModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentBank({
      ...currentBank,
      [name]: type === 'checkbox' ? checked : value
    });
    // Hata temizle
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!currentBank.name.trim()) {
      newErrors.name = 'Banka adı gereklidir';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (editMode) {
        await axiosConfig.put(
          `/admin/banks/${currentBank.id || currentBank._id}`,
          currentBank
        );
        alert('Banka başarıyla güncellendi.');
      } else {
        await axiosConfig.post('/admin/banks', currentBank);
        alert('Yeni banka başarıyla eklendi.');
      }
      
      fetchBanks();
      toggleModal();
    } catch (error) {
      console.error('İşlem hatası:', error);
      alert(error.response?.data?.message || 'İşlem sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu bankayı silmek istediğinizden emin misiniz?')) return;
    
    setLoading(true);
    try {
      await axiosConfig.delete(`/admin/banks/${id}`);
      alert('Banka başarıyla silindi.');
      fetchBanks();
      } catch (error) {
        console.error('Silme hatası:', error);
        alert('Banka silinirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
  };

  const seedBanks = async () => {
    if (!window.confirm('Türkiye\'deki popüler bankaları eklemek istiyor musunuz?')) return;
    
    setLoading(true);
    try {
      await axiosConfig.post('/admin/banks/seed', {});
      alert('Varsayılan bankalar başarıyla eklendi.');
      fetchBanks();
      } catch (error) {
        console.error('Seed hatası:', error);
        alert('Bankalar eklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-2 sm:p-4 md:p-6">
      <div className="w-full bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl overflow-hidden">
        {/* Üst başlık */}
        <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3 sm:gap-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white flex items-center">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              Banka Yönetimi
            </h2>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {banks.length === 0 && (
                <button
                  className="bg-yellow-500 text-white px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 rounded-lg hover:bg-yellow-600 transition-all duration-200 flex items-center shadow-lg text-sm sm:text-base"
                  onClick={seedBanks}
                  disabled={loading}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Varsayılan Bankaları Ekle
                </button>
              )}
              <button
                className="bg-blue-500 text-white px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 rounded-lg hover:bg-blue-600 transition-all duration-200 flex items-center shadow-lg text-sm sm:text-base"
                onClick={fetchBanks}
                disabled={loading}
              >
                <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                className="bg-emerald-500 text-white px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 rounded-lg hover:bg-emerald-600 transition-all duration-200 flex items-center shadow-lg text-sm sm:text-base"
                onClick={toggleModal}
                disabled={loading}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Yeni Banka Ekle
              </button>
            </div>
          </div>
        </div>

        {/* İçerik */}
        <div className="p-4 sm:p-6">
          {loading && banks.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : banks.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz banka eklenmemiş</h3>
              <p className="text-gray-500">Yukarıdaki "Varsayılan Bankaları Ekle" butonunu kullanarak hızlıca başlayabilirsiniz.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">#</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Banka Adı</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Banka Kodu</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Durum</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {banks.map((bank, index) => (
                    <tr key={bank.id || bank._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-3 text-sm text-gray-900">{index + 1}</td>
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">{bank.name}</td>
                      <td className="px-3 py-3 text-sm text-gray-600">{bank.code || '-'}</td>
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          bank.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {bank.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEdit(bank)}
                            className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Düzenle"
                          >
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(bank.id || bank._id)}
                            className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                            title="Sil"
                          >
                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-xl font-bold text-white">
                {editMode ? 'Banka Düzenle' : 'Yeni Banka Ekle'}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banka Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={currentBank.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Örn: Ziraat Bankası"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banka Kodu (SWIFT)
                </label>
                <input
                  type="text"
                  name="code"
                  value={currentBank.code}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Örn: TCZBTR2A"
                />
              </div>

              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={currentBank.isActive}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Aktif</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={toggleModal}
                  className="px-5 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  disabled={loading}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all font-medium shadow-lg"
                  disabled={loading}
                >
                  {loading ? 'İşleniyor...' : editMode ? 'Güncelle' : 'Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankManagement;
